import { Component, OnInit, Inject, OnDestroy } from "@angular/core";
import { FormGroup, FormControl, Validators, FormArray } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ApexAxisChartSeries } from "apexcharts";
import { startOfYear, endOfYear, isWithinInterval, isSameMonth, eachYearOfInterval, eachQuarterOfInterval, format, eachMonthOfInterval, Locale, differenceInDays, differenceInMonths, addMonths, endOfMonth, getYear, Interval, addDays, startOfDay } from "date-fns";
import { es, pt } from "date-fns/locale";
import { ISMultiSelectOption, ISSelectDatasource, ISSelectOption } from "is-lib";
import { ServerResponse } from "is-lib/lib/models/ServerResponse";
import * as _ from "lodash";
import { BehaviorSubject, catchError, EMPTY, finalize, forkJoin, from, map, mergeMap, Observable, of, Subject, switchMap, takeUntil, tap, throwError } from "rxjs";
import { CustomValidators } from "src/app/functions/form-validators";
import { GroupingSimulation } from "src/app/models/ContractPrice";
import { ElectricityMeter, IElectricityMeter } from "src/app/models/EnergyMeter";
import { ICreateContractElectricitySupplyProposal, ICreateElectricitySupplyProposal, ICreateElectricitySupplyProposalEconomicCondition, ICreateNaturalGasSupplyProposalEconomicCondition, ICreateNaturalGasSupplyProposalFixedEconomicCondition, ICreateNaturalGasSupplyProposalIndexedEconomicCondition, ICreateElectricitySupplyProposalFixedEconomicCondition, ICreateElectricitySupplyProposalIndexedEconomicCondition, ICreateOpportunityEnergySupplyProposal, ICreateNaturalGasSupplyProposal } from "src/app/models/EnergySupplyProposals";
import { PricePeriods } from "src/app/models/PricePeriod";
import { SnackBarType } from "src/app/models/Snackbar";
import { ClientPortalUsersService } from "src/app/services/client-portal-users.service";
import { ContractsService } from "src/app/services/contracts.service";
import { EnergySupplyService } from "src/app/services/energySupplies.service";
import { OpportunitiesService } from "src/app/services/opportunities.service";
import { PriceService } from "src/app/services/prices.service";
import { SnackBarService } from "src/app/services/snackbar.service";
import { SupplierProductEconomicConditionsService } from "src/app/services/supplierProductEconomicConditions.service";
import { UsersService } from "src/app/services/users.service";
import { environment } from "src/environments/environment";


@Component({
    templateUrl: 'cover-proposal-dialog.component.html',
    styleUrls: ['cover-proposal-dialog.component.scss']
})
export class CoverProposalDialog implements OnInit, OnDestroy {
    // Ends the translation stream subscription, which never completes on its own (it would keep every closed dialog and its cloned data)
    private readonly _destroy$ = new Subject<void>();

    steps: any[] = [];
    currentFormStep: number = 0;
    
    opportunityId?: string;
    // Set when the simulation is on an existing contract: the proposal is then created on the contract and its supplies
    contractId?: string;
    contractTypeId?: string;
    equipments: GroupingSimulation.IEquipment[] = [];
    selectedPrices: PricePeriods.ElectricityPrices[] = []
    series: ApexAxisChartSeries = []
    isElectricity: boolean = false;

    opportunity: any = null;

    hasContractDuration: boolean = false;
    allowedPaymentTypes: ISSelectOption[] = [];
    indexedPriceTypeId: string = '';
    fixedPriceTypeId: string = '';
    mixedPriceTypeId: string = '';
    hasIndexedPriceBase: boolean = false;

    selectedYear: Date | undefined;
    totalInterval: Interval;
    
    form = new FormGroup({
        paymentType: new FormControl<number | null>(null, Validators.required),
        electronicInvoice: new FormControl<boolean>(false, Validators.required),
        invoiceEmail: new FormControl<string | null>({ value: null, disabled: true }, Validators.email),
        isGreenEnergy: new FormControl<boolean>(false),
        loyaltyPeriod: new FormControl<number | null>({ value: null, disabled: true}),
    });

    economicConditionsFilters: {
        spGeneralConditionId?: number;
        spElectricityEconomicConditionId: number | null;
        spNaturalGasEconomicConditionId: number | null;
        spParticularConditionId?: number;
        supplierProductId?: number;
    } = {
        spGeneralConditionId: undefined,
        spElectricityEconomicConditionId: null,
        spNaturalGasEconomicConditionId: null,
        spParticularConditionId: undefined,
        supplierProductId: undefined
    }

    priceEquipmentsDisplayObject: any[] = []

    proposalId?: string;

    // Recap of the hedging view choices, display only
    summary: {
        prices: { id: string, label: string, description: string, coverPercentage: number, price: number }[],
        years: { year: number, coverPercentage: number, totalCostEstimate: number, averagePriceEstimate: number }[]
    } = { prices: [], years: [] };

    conditionsLoaded: boolean = false;
    submitting: boolean = false;
    greenEnergyAvailable: boolean = true;
    electronicInvoiceRequired: boolean = false;

    userLocale: Locale;

    private getIndexedCoverIntervals() {
        const monthlyCoverIntervals = eachMonthOfInterval(this.totalInterval).map((month, monthIndex, months) => {
            const monthCover = this.selectedPrices.reduce((sum, price) => {
                if (!isWithinInterval(month, { start: price.deliveryStartDate, end: price.deliveryEndDate })) {
                    return sum;
                }

                return sum + price.coverPercentage;
            }, 0);

            return {
                estimatedStartDate: (monthIndex === 0 ? this.totalInterval.start : month) as Date,
                estimatedEndDate: (monthIndex === months.length - 1 ? this.totalInterval.end : endOfMonth(month)) as Date,
                coverageIsFull: monthCover == 100
            };
        });

        return monthlyCoverIntervals.reduce((acc, interval) => {
            const lastInterval = acc[acc.length - 1];

            if (lastInterval && lastInterval.coverageIsFull === interval.coverageIsFull) {
                lastInterval.estimatedEndDate = interval.estimatedEndDate;
                return acc;
            }

            acc.push(interval);

            return acc;
        }, [] as { estimatedStartDate: Date; estimatedEndDate: Date; coverageIsFull: boolean }[])
    }

    constructor(
        private readonly dialogRef: MatDialogRef<CoverProposalDialog>,
        private _user: UsersService,
        private _priceService: PriceService,
        private _translateService: TranslateService,
        private _opportunityService: OpportunitiesService,
        private _energySuppliesService: EnergySupplyService,
        private _spEconomicConditionService: SupplierProductEconomicConditionsService,
        private _snackbarService: SnackBarService,
        private _userService: UsersService,
        private _contractsService: ContractsService,
        @Inject(MAT_DIALOG_DATA) private readonly data: any
    ) {
        this.userLocale = this._userService.userCountryLocale;

        _translateService.stream(['PAYMENT_DATA','CONFIRMATION'])
            .pipe(takeUntil(this._destroy$))
            .subscribe((trStream) => {
                this.steps = [ //Steps in form
                    { label: trStream['PAYMENT_DATA'], icon: 'upload' },
                    { label: trStream['CONFIRMATION'], icon: 'file-check' }
                ];
            })
        
        if (data) {
            this.equipments = _.cloneDeep(data.equipments);
            this.selectedPrices = _.cloneDeep(data.selectedPrices);
            this.opportunityId = data.opportunityId;
            this.contractId = data.contractId ?? undefined;
            this.series = _.cloneDeep(data.series)
            this.isElectricity = data.selectedMarket == 'Electricity';
            this.contractTypeId = data.contractTypeId;
            this.economicConditionsFilters = _.cloneDeep(data.economicConditions);
            this.summary = data.summary ?? {
                prices: this.selectedPrices.map((price) => ({ id: price.id, label: price.description, description: '', coverPercentage: price.coverPercentage, price: price.basePrice })),
                years: []
            };

            this.totalInterval = {start: _.minBy(this.selectedPrices, (price) => price.deliveryStartDate)?.deliveryStartDate ?? 0, end: _.maxBy(this.selectedPrices, (price) => price.deliveryEndDate)?.deliveryEndDate ?? 0}
        } else {
            this.totalInterval = {start: 0, end: 0}
        }


    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    ngOnInit(): void {
        this.form.controls.electronicInvoice.valueChanges.subscribe( (elInvoice) => {
            if (elInvoice == true) {
                this.form.controls.invoiceEmail.enable();
                if (String.isNullOrUndefinedOrWhitespace(this.form.controls.invoiceEmail.value)) {
                    this.form.controls.invoiceEmail.setValue(this._user.userData!.email)
                }
            } else {
                this.form.controls.invoiceEmail.disable();
            }
        });

        if (this.economicConditionsFilters.spElectricityEconomicConditionId && this.economicConditionsFilters.spParticularConditionId) {
            forkJoin({
                productElectricityEconomicCondition: this._spEconomicConditionService.getProductElectricityEconomicCondition(this.economicConditionsFilters.spElectricityEconomicConditionId).pipe( catchError(err => EMPTY) ),
                particularCondition: this._spEconomicConditionService.getParticularCondition(this.economicConditionsFilters.spParticularConditionId).pipe( catchError(err => EMPTY) )
            }).pipe(
                map( srs => ({
                    spElecEconomicCondition: srs.productElectricityEconomicCondition?.data,
                    spParticularCondition: srs.particularCondition?.data
                }))
            ).subscribe({ complete: () => this.conditionsLoaded = true, next: (spData) => {
                if (spData?.spParticularCondition) {
                    this.allowedPaymentTypes = spData?.spParticularCondition?.configurations?.allowedPaymentTypes?.map( (payType: any) => new ISSelectOption(payType.id, payType.invariantCultureDescription) ) ?? []

                    this.hasIndexedPriceBase = spData?.spElecEconomicCondition?.configurations?.hasBaseIndexedEconomicCondition;

                    if (!spData.spParticularCondition?.electricityConfigurations?.forGreenEnergy) {
                        this.greenEnergyAvailable = false;
                        this.form.controls.isGreenEnergy.disable();
                    }

                    if (spData.spParticularCondition.configurations?.hasRequiredElectronicInvoice) {
                        this.electronicInvoiceRequired = true;
                        this.form.controls.electronicInvoice.setValue(true);
                        this.form.controls.electronicInvoice.disable();
                    }



                    if (spData?.spParticularCondition?.electricityConfigurations?.hasLoyaltyPeriod ||
                    spData?.spParticularCondition?.electricityConfigurations?.minimumDurationMonths ||
                    spData?.spParticularCondition?.electricityConfigurations?.maximumDurationMonths) {
                        let minimumDuration = spData?.spParticularCondition?.electricityConfigurations.minimumDurationMonths ?? 0;
                        let maximumDuration = spData?.spParticularCondition?.electricityConfigurations.maximumDurationMonths ?? 24;

                        this.form.controls.loyaltyPeriod.enable();
                        this.form.controls.loyaltyPeriod.addValidators([ Validators.required, Validators.min(minimumDuration), Validators.max(maximumDuration) ]);
                    }
                }
            }})
        } else {
            this.conditionsLoaded = true;
        }

        this._energySuppliesService.getContractedPriceTypes().subscribe((sr) => {
            if (sr.data) {
                sr.data.forEach( (priceType) => {
                    if (priceType.invariantCultureDescription == 'Fixed Price') {
                        this.fixedPriceTypeId = priceType.id;
                    }

                    if (priceType.invariantCultureDescription == 'Indexed Price') {
                        this.indexedPriceTypeId = priceType.id;
                    }

                    if (priceType.invariantCultureDescription == 'Mixed Price') {
                        this.mixedPriceTypeId = priceType.id;
                    }
                } );
            }
        })
    }

    getAllowedPaymentTypesAsOptions(priceId: string) {
        return this.selectedPrices.find(price => price.id == priceId)?.spParticularCondition?.configurations.allowedPaymentTypes.map( (payType) => new ISSelectOption(payType.id, payType.invariantCultureDescription) );
    }

    priceSimulationIsElectricity(priceSimulation: GroupingSimulation.IFixedPriceSimulation): priceSimulation is GroupingSimulation.ElectricityFixedPriceSimulation {
        return Object.hasOwn(priceSimulation, 'peakPrice');
    }

    priceSimulationIsNaturalGas(priceSimulation: GroupingSimulation.IFixedPriceSimulation): priceSimulation is GroupingSimulation.NaturalGasFixedPriceSimulation {
        return !Object.hasOwn(priceSimulation, 'peakPrice');
    }

    get isContract(): boolean {
        return !String.isNullOrUndefinedOrWhitespace(this.contractId);
    }

    // True while the button can't submit yet: no payment type or an invalid invoice email, or for a contract (which keeps its own billing terms)
    // until the product conditions have loaded, since they tell whether there's an indexed base price
    get submitBlocked(): boolean {
        if (this.isContract) {
            return this.submitting || !this.conditionsLoaded;
        }

        return this.submitting || this.form.controls.paymentType.invalid || this.form.controls.invoiceEmail.invalid;
    }

    submitProposal() {
        if (this.submitBlocked) {
            return;
        }

        if (!this.hasIndexedPriceBase) {
            this._snackbarService.callSnackBar('NO_INDEXED_PRICE_BASE', SnackBarType.ERROR);
            return;
        }

        // In case there's an error associating equipments and the proposal was already created makes it so the request to create a proposal is not made again.
        if (!String.isNullOrUndefinedOrWhitespace(this.proposalId)) {
            this.submitting = true;
            this.createElectricityProposalsRequest(this.proposalId).subscribe({
                next: (results) => {
                    const failedEquipments = results;
                    this.submitting = false;

                    if (failedEquipments.length > 0) {
                        this._snackbarService.callSnackBar(`ERROR_ASSOCIATING_ELECTRICITY_METERS_WITH_PARAMS`, SnackBarType.ERROR, undefined, undefined, undefined, {failedEquipments: failedEquipments.join(', ')});
                    } else {
                        this._snackbarService.callSnackBar(`PROPOSAL_CREATED_SUCCESSFULLY`, SnackBarType.SUCCESS);
                        this.dialogRef.close(this.proposalId ?? null);
                    }
                },
                error: (error) => {
                    this.submitting = false;
                    this._snackbarService.callSnackBar('ERROR_CREATING_PROPOSAL', SnackBarType.ERROR);
                }
            });
        } else {
            const proposalCreation$ = this.createEnergySupplyProposalRequest();

            if (!proposalCreation$) {
                return;
            }

            this.submitting = true;
            proposalCreation$.pipe(
                catchError((error) => {
                    return throwError(() => new Error('Proposal creation failed'));
                }),
                switchMap( (sr: ServerResponse<any>) => {
                    if (sr && sr.data && sr.data.id != null) {
                        let proposalId = sr.data.id;
                        this.proposalId = proposalId;
                        
                        return this.createElectricityProposalsRequest(proposalId);
                    }

                    return EMPTY;
                }),
                // Covers the EMPTY path above, which completes without next/error
                finalize(() => this.submitting = false)
            ).subscribe({
                next: (results) => {
                    const failedEquipments = results;

                    if (failedEquipments.length > 0) {
                        this._snackbarService.callSnackBar(`ERROR_ASSOCIATING_ELECTRICITY_METERS_WITH_PARAMS`, SnackBarType.ERROR, undefined, undefined, undefined, {failedEquipments: failedEquipments.join(', ')});
                    } else {
                        this._snackbarService.callSnackBar(`PROPOSAL_CREATED_SUCCESSFULLY`, SnackBarType.SUCCESS);
                        this.dialogRef.close(this.proposalId ?? null);
                    }
                },
                error: (error) => {
                    this._snackbarService.callSnackBar('ERROR_CREATING_PROPOSAL', SnackBarType.ERROR);
                }
            });
        }
    }

    // Creates the energy supply proposal on the contract or on the opportunity. Undefined when the selected conditions are missing what it needs.
    createEnergySupplyProposalRequest(): Observable<ServerResponse<any>> | undefined {
        if (this.isContract) {
            // The contract already has its own billing terms, so only the economic condition of the selected prices is sent
            if (this.economicConditionsFilters.spElectricityEconomicConditionId == null) {
                return undefined;
            }

            return this._contractsService.createContractEnergySupplyProposal(this.contractId!, {
                spElectricityEconomicConditionId: this.economicConditionsFilters.spElectricityEconomicConditionId
            });
        }

        if (this.economicConditionsFilters.spGeneralConditionId != null && this.economicConditionsFilters.spParticularConditionId != null && this.economicConditionsFilters.supplierProductId != null) {
            let proposalObject: ICreateOpportunityEnergySupplyProposal = {
                contractTypeId: this.contractTypeId!,
                paymentTypeId: this.form.controls.paymentType.value!,
                spGeneralConditionId: this.economicConditionsFilters.spGeneralConditionId,
                spParticularConditionId: this.economicConditionsFilters.spParticularConditionId,
                supplierProductId: this.economicConditionsFilters.supplierProductId,
                spElectricityEconomicConditionId: this.economicConditionsFilters.spElectricityEconomicConditionId ?? undefined,
                withElectronicInvoice: this.form.controls.electronicInvoice.value!,
                emailToSendInvoices: this.form.controls.invoiceEmail.value ?? undefined
            }

            return this._opportunityService.createOpportunityEnergySupplyProposal(this.opportunityId!, proposalObject);
        }

        return undefined;
    }

    createElectricityProposalsRequest(proposalId: string) {
        if (this.isElectricity) {
            let earliestPriceDate: Date = _.minBy(this.selectedPrices, (price) => price.deliveryStartDate.getTime())!.deliveryStartDate;
            let latestPriceDate: Date = _.maxBy(this.selectedPrices, (price) => price.deliveryEndDate.getTime())!.deliveryEndDate;

            // The contract endpoints take ISO dates
            let formatDate = (date: Date) => { return format(date, this.isContract ? 'yyyy-MM-dd' : 'MM/dd/yyyy')};

            let equipmentIndexedEconomicConditions: ICreateElectricitySupplyProposalEconomicCondition = {
                contractedPriceTypeId: this.indexedPriceTypeId,
                estimatedStartDate: formatDate(earliestPriceDate),
                estimatedEndDate: this.form.controls.loyaltyPeriod.enabled ? formatDate(latestPriceDate) : undefined,
                fixedEconomicCondition: undefined,
                indexedEconomicCondition: {
                    energyOperatingExpense: 0
                }
            }
            
            let equipmentFixedEconomicConditions = this.selectedPrices.map((price) => ({
                    contractedPriceTypeId: this.fixedPriceTypeId,
                    estimatedStartDate: formatDate(price.deliveryStartDate),
                    estimatedEndDate: formatDate(price.deliveryEndDate),
                    fixedEconomicCondition: {
                        fixedPercentage: price.coverPercentage,
                        energyOperatingExpense: 0
                    }
                })
            );

            // Same indexed base + one fixed condition per locked price for both kinds of proposal
            let economicConditions: ICreateElectricitySupplyProposalEconomicCondition[] = [equipmentIndexedEconomicConditions, ...equipmentFixedEconomicConditions];

            let electricityProposalRequests = (this.equipments as GroupingSimulation.ElectricityEquipment[]).map( (equip) => ({
                equipment: equip,
                // On a contract the equipment guid is the contract's electricity supply id (see GroupingViewComponent.getContractElectricityMeters)
                request$: this.isContract
                    ? this._contractsService.associateElectricitySupplyToContractEnergySupplyProposal({
                        contractElectricitySupplyId: equip.guid!,
                        economicConditions: economicConditions
                    } as ICreateContractElectricitySupplyProposal, proposalId)
                    : this._opportunityService.associateElectricityMeterToEnergySupplyProposal({
                        electricityCountTypeId: equip.countingCycleId,
                        electricityTensionLevelId: equip.tensionLevelId,
                        electricityTimeCycleId: equip.timeCycleId,
                        contractedPower: equip.power,
                        energyOpportunityElectricityMeterId: equip.guid,
                        isGreenEnergySupply: this.form.controls.isGreenEnergy.value,
                        estimatedSupplyStartDate: formatDate(earliestPriceDate),
                        economicConditions: economicConditions,
                        supplyContractDurationDays: this.form.controls.loyaltyPeriod.enabled ? differenceInDays(latestPriceDate, earliestPriceDate) + 1 : null
                    } as ICreateElectricitySupplyProposal, proposalId)
            }))

            return forkJoin(
                electricityProposalRequests.map((electricityProposalRequest) =>
                    electricityProposalRequest.request$.pipe(
                        catchError((error) => {
                            return of({error: true, cpeNumber: electricityProposalRequest.equipment.code});
                        })
                    )
                )
            ).pipe(
                map((results) => results.filter(r => r.error).map(r => r.cpeNumber)),
                catchError((error) => {
                    this._snackbarService.callSnackBar('ERROR_ASSOCIATING_ELECTRICITY_METERS', SnackBarType.ERROR);
                    return throwError(() => error);
                })
            );
        } else {
            return EMPTY;
        }
    }

    closeDialog(dialogReturn: boolean) {
        this.dialogRef.close(dialogReturn);
    }

    // Supply period covered by the locked prices, as Dates for the dateRange pipe
    get supplyPeriod(): [Date, Date] {
        return [new Date(this.totalInterval.start), new Date(this.totalInterval.end)];
    }

    // Closes without a result so the hedging view doesn't navigate anywhere
    cancel() {
        this.dialogRef.close(null);
    }

    protected getPriceById(priceId: string) {
        return this.selectedPrices.find( price => price.id == priceId );
    }

    get selectedPricesTotalInterval(): Interval | undefined {
        if (this.selectedPrices.length == 0) {
            return undefined;
        }

        let start = _.minBy(this.selectedPrices, (price) => price.deliveryStartDate)?.deliveryStartDate ?? 0
        let end = _.maxBy(this.selectedPrices, (price) => price.deliveryEndDate)?.deliveryEndDate ?? 0

        if (differenceInMonths(end, start) < 11) {
            if (getYear(end) == getYear(start)) {
                end = endOfYear(start)
            }
            start = startOfYear(start);
        }

        return { 
            start: start,
            end: end
        }
    }
}